/**
 * What is likely to come out wrong in the printed PDF, worked out *before* the export runs
 * (docs/10-ux-audit.md · UX-32).
 *
 * Two halves, deliberately separated:
 *
 *  - `checkReadiness` is pure. It looks at the document, the photo map and the export options the
 *    person is about to use, and reports the things that can be decided from numbers alone: a photo
 *    the renderer will not be able to read, a photo frame nobody filled, and an effective print
 *    resolution below what a press can hold. The resolution figure is the renderer's own arithmetic
 *    (`computeImageLayout` in @freealbum/render): "cover" the frame from the photo's real pixels,
 *    multiply by the crop zoom, and cap the result at the DPI the page is rasterised with — no
 *    photo can print sharper than the page it sits on.
 *
 *  - `measureTextIssues` is not pure and cannot be: whether a paragraph fits its box depends on the
 *    font's real metrics. It measures in the browser, with the same font files, sizes, line heights
 *    and letter spacing the renderer uses, after `document.fonts.ready` — and when a face is not
 *    actually available it says nothing rather than guessing with a fallback font's metrics.
 *
 * Severity is about what the person can do: a `blocker` will definitely be wrong in the file
 * (a hole where a photo should be), a `warning` may well be on purpose (a blank page, an
 * oversized word) and is therefore dismissible in the dialog.
 */
import {
  imageCoverScale,
  neutralImageFit,
  type AlbumDocument,
  type Background,
  type Element,
  type ExportOptions,
  type ImageFit,
  type PhotoMap,
  type TextContent,
  type TextStyle,
} from '@freealbum/document';
import type { Target } from './types';

export type ReadinessSeverity = 'blocker' | 'warning';

export type ReadinessKind = 'photoUnavailable' | 'emptySlot' | 'lowResolution' | 'textOverflow';

export interface ReadinessIssue {
  /** Stable across re-checks of the same document, so a dismissal keeps sticking. */
  key: string;
  kind: ReadinessKind;
  severity: ReadinessSeverity;
  /** Which surface it is on — a spread index, or the cover. */
  target: Target;
  /** The element to select when jumping there; null when the offender is a page background. */
  elementId: string | null;
  photoId?: string;
  /** `lowResolution` only: the effective output resolution in DPI, rounded. */
  dpi?: number;
}

/**
 * Below this an image starts to look soft on paper. 300 is the press ideal and 150 is roughly the
 * point where a viewer at arm's length can tell, so 150 is where it is worth saying something.
 */
export const MIN_PRINT_DPI = 150;

const MM_PER_INCH = 25.4;
const MM_PER_PT = 25.4 / 72;

// ── the pure checks ──────────────────────────────────────────────────────────

/**
 * Output resolution of one placed photo, in DPI.
 *
 * Mirrors `computeImageLayout`: the image is sized to *cover* the frame from its own pixels and
 * then zoomed by `fit.scale`, so one source pixel occupies `cover × scale` millimetres of paper.
 * `rotate90` swaps the aspect ratio, exactly as the renderer does.
 */
export function effectiveDpi(args: {
  photo: { width: number; height: number };
  frame: { wMm: number; hMm: number };
  fit: Pick<ImageFit, 'scale' | 'rotate90'> & Partial<Pick<ImageFit, 'straightenDeg'>>;
  /** the DPI the export will rasterise the page at */
  exportDpi: number;
}): number {
  const { photo, frame, fit, exportDpi } = args;
  if (!(frame.wMm > 0 && frame.hMm > 0)) return exportDpi;
  // The renderer's own geometry: a straightened picture is scaled up to keep the corners covered,
  // so it spends more paper per source pixel and its DPI drops accordingly.
  const mmPerPixel = imageCoverScale({
    boxWidth: frame.wMm,
    boxHeight: frame.hMm,
    naturalWidth: photo.width,
    naturalHeight: photo.height,
    fit: { ...neutralImageFit(), scale: fit.scale, rotate90: fit.rotate90, straightenDeg: fit.straightenDeg ?? 0 },
  });
  if (!(mmPerPixel > 0)) return exportDpi;
  return Math.min(MM_PER_INCH / mmPerPixel, exportDpi);
}

const targetKey = (target: Target): string => (target === 'cover' ? 'cover' : `spread-${target}`);
const targetOrder = (target: Target): number => (target === 'cover' ? -1 : target);

/** Document order: the cover, then the spreads. Stable, so callers can group without reshuffling. */
export function sortIssues(issues: readonly ReadinessIssue[]): ReadinessIssue[] {
  return [...issues].sort((a, b) => targetOrder(a.target) - targetOrder(b.target));
}

export const hasBlockers = (issues: readonly ReadinessIssue[]): boolean => issues.some((i) => i.severity === 'blocker');

interface PhotoPlacement {
  target: Target;
  elementId: string | null;
  /** distinguishes the two backgrounds of one spread from each other and from its elements */
  slot: string;
  photoId: string;
  frame: { wMm: number; hMm: number };
  fit: Pick<ImageFit, 'scale' | 'rotate90'>;
}

function checkPlacedPhoto(out: ReadinessIssue[], place: PhotoPlacement, photos: PhotoMap, exportDpi: number): void {
  const base = { target: place.target, elementId: place.elementId, photoId: place.photoId };
  const info = photos[place.photoId];
  // Gone from the tray, failed to process, or purged for inactivity: the renderer has nothing to
  // draw and will leave a hole.
  if (!info || info.status === 'failed' || info.status === 'purged') {
    out.push({ key: `photoUnavailable:${targetKey(place.target)}:${place.slot}`, kind: 'photoUnavailable', severity: 'blocker', ...base });
    return;
  }
  const dpi = effectiveDpi({ photo: info, frame: place.frame, fit: place.fit, exportDpi });
  if (dpi < MIN_PRINT_DPI) {
    out.push({ key: `lowResolution:${targetKey(place.target)}:${place.slot}`, kind: 'lowResolution', severity: 'warning', dpi: Math.round(dpi), ...base });
  }
}

function checkSurface(
  out: ReadinessIssue[],
  target: Target,
  elements: readonly Element[],
  backgrounds: readonly { slot: string; bg: Background | null; frame: { wMm: number; hMm: number } }[],
  photos: PhotoMap,
  exportDpi: number,
): void {
  for (const { slot, bg, frame } of backgrounds) {
    if (!bg || bg.type !== 'photo') continue;
    checkPlacedPhoto(out, { target, elementId: null, slot, photoId: bg.photoId, frame, fit: bg.fit }, photos, exportDpi);
  }
  for (const el of elements) {
    if (el.type !== 'photo' || el.hidden) continue;
    if (!el.photoId) {
      // A slot from a layout that nobody filled. Often deliberate — hence a dismissible warning.
      out.push({ key: `emptySlot:${targetKey(target)}:${el.id}`, kind: 'emptySlot', severity: 'warning', target, elementId: el.id });
      continue;
    }
    checkPlacedPhoto(out, { target, elementId: el.id, slot: el.id, photoId: el.photoId, frame: el.frame, fit: el.fit }, photos, exportDpi);
  }
}

/** Everything that can be decided without a browser. Text is handled by `measureTextIssues`. */
export function checkReadiness(
  doc: AlbumDocument,
  photos: PhotoMap,
  options: Pick<ExportOptions, 'dpi' | 'cover'>,
): ReadinessIssue[] {
  const out: ReadinessIssue[] = [];
  const exportDpi = options.dpi;
  const page = doc.page;

  // The cover is only worth checking when this export will actually contain one.
  if (doc.cover.enabled && options.cover !== 'none') {
    const spine = doc.cover.spineWidthMm;
    checkSurface(
      out,
      'cover',
      doc.cover.elements,
      [
        { slot: 'bg', bg: doc.cover.background, frame: { wMm: page.widthMm * 2 + spine, hMm: page.heightMm } },
        { slot: 'bg-spine', bg: doc.cover.spineBackground, frame: { wMm: spine, hMm: page.heightMm } },
      ],
      photos,
      exportDpi,
    );
  }

  doc.spreads.forEach((spread, index) => {
    // A lone page prints half a spread, and a per-page background covers one page rather than two.
    const spansSpread = !spread.single && !spread.backgroundRight;
    const backgrounds = [
      { slot: 'bg', bg: spread.background, frame: { wMm: spansSpread ? page.widthMm * 2 : page.widthMm, hMm: page.heightMm } },
      { slot: 'bg-right', bg: spread.backgroundRight, frame: { wMm: page.widthMm, hMm: page.heightMm } },
    ];
    checkSurface(out, index, spread.elements, backgrounds, photos, exportDpi);
  });

  return sortIssues(out);
}

// ── the measured check ───────────────────────────────────────────────────────

/** px per mm used for measuring. Big enough that rounding cannot invent an overflowing line. */
const MEASURE_PX_PER_MM = 4;
/** A paragraph block has to miss its box by this much (in mm) before it is worth mentioning. */
const OVERFLOW_TOLERANCE_MM = 0.5;

interface TextBox {
  target: Target;
  elementId: string;
  /** distinguishes a photo's caption band from the photo element itself */
  slot: string;
  content: TextContent;
  style: TextStyle;
  wMm: number;
  hMm: number;
}

function collectTextBoxes(doc: AlbumDocument): TextBox[] {
  const out: TextBox[] = [];
  const visit = (target: Target, elements: readonly Element[]) => {
    for (const el of elements) {
      if (el.hidden) continue;
      if (el.type === 'text') {
        out.push({ target, elementId: el.id, slot: el.id, content: el.content, style: el.style, wMm: el.frame.wMm, hMm: el.frame.hMm });
      } else if (el.type === 'photo' && el.caption) {
        // below/above hang outside the frame, overlay-* sit inside it; either way the band is
        // `heightMm` tall and as wide as the photo.
        out.push({ target, elementId: el.id, slot: `${el.id}:caption`, content: el.caption.content, style: el.caption.style, wMm: el.frame.wMm, hMm: el.caption.heightMm });
      }
    }
  };
  visit('cover', doc.cover.elements);
  doc.spreads.forEach((spread, index) => visit(index, spread.elements));
  return out.filter((box) => box.content.paragraphs.some((p) => p !== ''));
}

const familyCss = (family: string): string => `"${family.replace(/["\\]/g, '')}"`;

/** A CSS font shorthand — what `FontFaceSet.load`/`check` want. */
function fontShorthand(style: TextStyle, px: number): string {
  return `${style.italic ? 'italic ' : ''}${style.fontWeight} ${px}px ${familyCss(style.fontFamily)}`;
}

/**
 * Measures every paragraph block against its own box, in the browser, with the real fonts.
 *
 * Returns nothing at all off the browser, without a `FontFaceSet`, or for any box whose font file
 * is not actually loaded: a wrong answer here is worse than no answer.
 */
export async function measureTextIssues(doc: AlbumDocument): Promise<ReadinessIssue[]> {
  if (typeof window === 'undefined' || typeof globalThis.document === 'undefined') return [];
  const dom = globalThis.document;
  const fonts = dom.fonts;
  if (!fonts) return [];

  const boxes = collectTextBoxes(doc);
  if (boxes.length === 0) return [];

  // Faces load lazily, and a spread nobody has opened has never asked for its font. Ask now, with
  // the characters that will be shaped, then wait for the whole set to settle.
  await Promise.all(
    boxes.map((box) =>
      fonts
        .load(fontShorthand(box.style, box.style.fontSizePt * MM_PER_PT * MEASURE_PX_PER_MM), box.content.paragraphs.join(' '))
        .catch(() => []),
    ),
  );
  await fonts.ready;

  const host = dom.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText = 'position:fixed;top:0;left:-20000px;width:0;height:0;overflow:hidden;pointer-events:none;';
  dom.body.appendChild(host);

  const out: ReadinessIssue[] = [];
  try {
    for (const box of boxes) {
      const style = box.style;
      const px = style.fontSizePt * MM_PER_PT * MEASURE_PX_PER_MM;
      if (!fonts.check(fontShorthand(style, px))) continue;

      const pad = style.paddingMm * MEASURE_PX_PER_MM;
      const innerW = box.wMm * MEASURE_PX_PER_MM - 2 * pad;
      const innerH = box.hMm * MEASURE_PX_PER_MM - 2 * pad;
      if (!(innerW > 0) || !(innerH > 0)) continue;

      const block = dom.createElement('div');
      block.style.cssText = `position:absolute;top:0;left:0;width:${innerW}px;`;
      for (const paragraph of box.content.paragraphs) {
        const p = dom.createElement('p');
        p.dir = style.direction;
        p.textContent = paragraph === '' ? '\u200b' : paragraph;
        p.style.margin = '0';
        p.style.fontFamily = familyCss(style.fontFamily);
        p.style.fontSize = `${px}px`;
        p.style.fontWeight = String(style.fontWeight);
        p.style.fontStyle = style.italic ? 'italic' : 'normal';
        p.style.lineHeight = String(style.lineHeight);
        if (style.letterSpacing !== 0) p.style.letterSpacing = `${style.letterSpacing}em`;
        p.style.textAlign = style.align;
        p.style.whiteSpace = 'pre-wrap';
        p.style.overflowWrap = 'break-word';
        p.style.unicodeBidi = style.direction === 'auto' ? 'plaintext' : 'isolate';
        block.appendChild(p);
      }
      host.appendChild(block);
      const height = block.getBoundingClientRect().height;
      host.removeChild(block);

      if (height > innerH + OVERFLOW_TOLERANCE_MM * MEASURE_PX_PER_MM) {
        out.push({ key: `textOverflow:${targetKey(box.target)}:${box.slot}`, kind: 'textOverflow', severity: 'warning', target: box.target, elementId: box.elementId });
      }
    }
  } finally {
    host.remove();
  }
  return out;
}
